import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Seo from "@/components/feature/Seo";
import ContactForm from "./components/ContactForm";
import { principles, testimonials } from "@/mocks/testimonials";
import { siteStats } from "@/mocks/categories";
import { events } from "@/mocks/events";

const SITE = "https://9jzs.cn";

const milestones = [
  { year: "2024", title: "从一个收藏夹开始", desc: "最初只是把看到的公开说明按顺序记下来，方便自己回看，后来发现分享给朋友也很有用。" },
  { year: "2025", title: "建立时间线模板", desc: "把「信息出现—相关方说明—热度回落」固定成三段式结构，档案开始具备统一的阅读节奏。" },
  { year: "2026", title: "上线分类与百科", desc: "按圈层划分六个分类，并整理常见概念，让第一次围观的人也能快速看懂讨论在说什么。" },
];

export default function About() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于瓜田观察站",
      description:
        "了解瓜田观察站的品牌定位、内容原则与工作方式：只整理公开信息，坚持不呈现个人隐私，把脉络讲清楚再谈观点。",
      url: `${SITE}/about`,
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "瓜田观察站",
      url: SITE,
      email: "contact@guatian-observer.example.com",
      description: "将公开的网络热点信息按分类归档、以时间线呈现的内容观察站。",
      knowsAbout: ["事件时间线", "公开信息整理", "内容观察", "网络讨论礼仪"],
    },
    {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "联系瓜田观察站",
      description: "通过订阅与线索投递表单联系瓜田观察站，提交热点线索、内容纠正或合作咨询。",
      url: `${SITE}/about#contact`,
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "关于我们", item: `${SITE}/about` },
      ],
    },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Seo
        title="关于我们 · 品牌定位与内容原则 · 瓜田观察站"
        description="瓜田观察站只整理公开信息，不呈现个人隐私。这里介绍我们的品牌定位、内容原则、更新流程与联系方式，欢迎投稿线索与纠错建议。"
        keywords="关于我们,品牌定位,内容原则,公开信息整理,联系方式,瓜田观察站"
        path="/about"
        jsonLd={jsonLd}
      />
      <SiteHeader />

      <main className="flex-1">
        <section className="w-full border-b border-background-200 bg-background-100 pb-12 pt-24 md:pb-16 md:pt-32">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
            <Breadcrumb items={[{ label: "关于我们" }]} />
            <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-[1.4fr_1fr] lg:items-center">
              <div>
                <p className="eyebrow">
                  <i className="ri-leaf-line" aria-hidden="true"></i>
                  关于瓜田观察站
                </p>
                <h1 className="mt-4 font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-5xl">
                  把围观变成一次有秩序的阅读
                </h1>
                <p className="mt-5 max-w-2xl font-body text-sm leading-7 text-foreground-600 md:text-base">
                  瓜田观察站是一个专注网络热点整理的内容站。我们不追最快的速度，而是追最清楚的脉络：
                  一件事什么时候出现、谁在什么时候说明、讨论的重心又转移到了哪里。信息整理清楚，
                  围观就不再只是情绪消耗。
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Link to="/topics" className="btn-primary">
                    浏览事件档案
                  </Link>
                  <a href="#contact" className="btn-secondary">
                    联系我们
                  </a>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {siteStats.map((stat) => (
                  <div key={stat.label} className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                    <i className={`${stat.icon} text-xl text-primary-500`} aria-hidden="true"></i>
                    <p className="mt-3 font-heading text-2xl font-bold text-foreground-950">{stat.value}</p>
                    <p className="mt-1 font-label text-xs text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-14">
              <div>
                <h2 className="section-title">我们为什么做这件事</h2>
                <div className="mt-5 flex flex-col gap-4 font-body text-sm leading-7 text-foreground-600 md:text-base">
                  <p>
                    热点话题的生命周期通常很短，几天之后再去翻，能看到的多半是结论，而不是过程。
                    于是同一件事会被反复讨论，却始终没人说得清最初发生了什么。
                  </p>
                  <p>
                    我们想补上这一块：用统一的模板把公开信息存下来。它既方便当下的读者快速了解，
                    也方便后来的人回溯。内容不必煽情，把时间点写准就已经很有价值。
                  </p>
                  <p>
                    这也是我们坚持不呈现个人隐私的原因。围观的对象应该是事件本身，而不是某个具体的人。
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">内容原则</h2>
                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {principles.map((item) => (
                    <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                      <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-700">
                        <i className={item.icon} aria-hidden="true"></i>
                      </span>
                      <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                      <p className="mt-2 font-body text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
            <h2 className="section-title">我们是怎么走到今天的</h2>
            <ol className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
              {milestones.map((item, index) => (
                <li key={item.year} className="relative rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="font-heading text-3xl font-bold text-primary-200">0{index + 1}</span>
                  <p className="mt-2 font-label text-xs tracking-wide text-secondary-600">{item.year}</p>
                  <h3 className="mt-2 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-3 font-body text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1fr] lg:gap-14">
              <div>
                <h2 className="section-title">读者怎么说</h2>
                <p className="mt-4 font-body text-sm leading-relaxed text-foreground-600 md:text-base">
                  这些反馈来自长期订阅的读者，也一直在提醒我们把「准确」放在「快」的前面。
                </p>
                <div className="mt-7 flex flex-col gap-5">
                  {testimonials.slice(0, 2).map((item) => (
                    <figure key={item.name} className="rounded-lg border border-background-200 bg-background-100 p-5">
                      <blockquote className="font-body text-sm leading-relaxed text-foreground-700">
                        「{item.content}」
                      </blockquote>
                      <figcaption className="mt-3 font-label text-xs text-foreground-500">
                        {item.name} · {item.role}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-5">
                <h2 className="section-title">最近在跟进的档案</h2>
                <ul className="flex flex-col divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
                  {events.slice(0, 4).map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/topic/${item.id}`}
                        className="group flex items-center gap-4 px-5 py-4 transition-colors hover:bg-background-100"
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                          <i className="ri-file-list-3-line" aria-hidden="true"></i>
                        </span>
                        <span className="flex-1">
                          <span className="block font-heading text-sm font-bold text-foreground-950 transition-colors group-hover:text-primary-600">
                            {item.title}
                          </span>
                          <span className="mt-0.5 block font-label text-xs text-foreground-500">
                            {item.categoryName} · {item.date} · {item.status}
                          </span>
                        </span>
                        <i className="ri-arrow-right-line text-foreground-400" aria-hidden="true"></i>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="w-full scroll-mt-24 bg-background-100 py-14 md:py-20">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-14">
              <div>
                <p className="eyebrow">
                  <i className="ri-customer-service-2-line" aria-hidden="true"></i>
                  联系我们
                </p>
                <h2 className="section-title mt-4">欢迎投递线索与建议</h2>
                <p className="mt-4 font-body text-sm leading-relaxed text-foreground-600 md:text-base">
                  无论是新的事件线索、档案里的错漏，还是想订阅我们的简报，都可以通过右侧表单提交。
                  请尽量附上公开来源链接，核实会更快。
                </p>
                <ul className="mt-7 flex flex-col gap-4">
                  <li className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className="ri-mail-line text-lg" aria-hidden="true"></i>
                    </span>
                    <span>
                      <strong className="block font-label text-sm font-medium text-foreground-950">邮箱</strong>
                      <span className="font-body text-sm text-foreground-600">
                        contact@guatian-observer.example.com
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                      <i className="ri-time-line text-lg" aria-hidden="true"></i>
                    </span>
                    <span>
                      <strong className="block font-label text-sm font-medium text-foreground-950">处理时间</strong>
                      <span className="font-body text-sm text-foreground-600">
                        工作日 10:00 - 19:00，通常 1-2 个工作日内回复
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                      <i className="ri-shield-check-line text-lg" aria-hidden="true"></i>
                    </span>
                    <span>
                      <strong className="block font-label text-sm font-medium text-foreground-950">隐私说明</strong>
                      <span className="font-body text-sm text-foreground-600">
                        你提交的信息仅用于回复与简报发送，不会公开展示
                      </span>
                    </span>
                  </li>
                </ul>
              </div>

              <ContactForm />
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}